<template>
  <div class="badge-level-picker" ref="pickerContainer">
    <!-- Label -->
    <div class="picker-field-label">Readiness Level*</div>

    <!-- Closed State / Trigger -->
    <div
      class="picker-trigger"
      :class="{ 'picker-trigger--open': isOpen }"
      @click.stop="toggleDropdown"
      tabindex="0"
      @keydown.enter.stop="toggleDropdown"
      @keydown.space.prevent.stop="toggleDropdown"
      @keydown.escape="closeDropdown"
    >
      <!-- Floating Label when open -->
      <div v-if="isOpen" class="picker-floating-label">Choose Readiness Level</div>
      
      <div class="picker-content">
        <div class="picker-selected-content">
          <div class="picker-checkbox" :class="getCheckboxClass(selectedLevel)"></div>
          <span class="picker-label">
            <span class="picker-label-name">{{ getSelectedLabelName() }}</span>
            <span class="picker-label-description">{{ getSelectedLabelDescription() }}</span>
          </span>
        </div>
      </div>
      <v-icon
        class="picker-chevron"
        :class="{ 'picker-chevron--rotated': isOpen }"
      >
        mdi-chevron-down
      </v-icon>
    </div>

    <!-- Dropdown Menu -->
    <div v-if="isOpen" class="picker-dropdown">
      <div class="picker-menu">
        <div
          v-for="option in readinessLevels"
          :key="option.value"
          class="picker-menu-item"
          :class="{
            'picker-menu-item--selected': selectedLevel === option.value,
          }"
          @click.stop="selectLevel(option.value, $event)"
          tabindex="0"
          @keydown.enter="selectLevel(option.value, $event)"
        >
          <div class="picker-menu-item-content">
            <div class="picker-menu-item-content-text">
              <div 
                class="picker-checkbox" 
                :class="getCheckboxClass(option.value)"
              >
              </div>
              <span class="picker-menu-label">{{ option.label }}</span>
              <span class="picker-menu-description">{{ option.description }}</span>
            </div>
            <svg
              v-if="selectedLevel === option.value"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              :class="`checkmark-${option.value}`"
            >
              <path
                d="M16.6673 5L7.50065 14.1667L3.33398 10"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "apprentice",
    validator: (value) => ["apprentice", "professional", "coach", "master"].includes(value),
  },
});

const emit = defineEmits(["update:modelValue", "change"]);

const pickerContainer = ref(null);
const isOpen = ref(false);

const readinessLevels = [
  {
    value: "apprentice",
    label: "Apprentice",
    description: "can do with supervision",
  },
  {
    value: "professional",  
    label: "Professional",
    description: "can do without supervision",
  },
  {
    value: "coach",
    label: "Coach",
    description: "can coach and inspect others",
  },
  {
    value: "master",
    label: "Master",
    description: "creates standards",
  },
];

const selectedLevel = ref(props.modelValue);

const getSelectedLabelName = () => {
  const option = readinessLevels.find((opt) => opt.value === selectedLevel.value);
  return option ? option.label : "";
};

const getSelectedLabelDescription = () => {
  const option = readinessLevels.find((opt) => opt.value === selectedLevel.value);
  return option ? option.description : "";
};


const getCheckboxClass = (level) => {
  const classes = {
    apprentice: "checkbox-apprentice",
    professional: "checkbox-professional",
    coach: "checkbox-coach",
    master: "checkbox-master",
  };
  return classes[level] || "";
};

// Sync with parent's modelValue prop changes
watch(
  () => props.modelValue,
  (newVal) => {
    selectedLevel.value = newVal;
  }
);

const toggleDropdown = (event) => {
  if (event) {
    event.stopPropagation();
  }
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const selectLevel = (levelValue, event) => {
  if (event) {
    event.stopPropagation();
  }
  selectedLevel.value = levelValue;
  emit("update:modelValue", levelValue);
  emit("change", levelValue);
  closeDropdown();
};

const handleClickOutside = (event) => {
  if (pickerContainer.value && !pickerContainer.value.contains(event.target)) {
    closeDropdown();
  }
};

onMounted(() => {
  // Use a small delay to avoid immediate closing when opening
  setTimeout(() => {
    document.addEventListener("click", handleClickOutside);
  }, 0);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.badge-level-picker {
  position: relative;
  width: 100%;
  max-width: 440px;
  font-family: Inter, sans-serif;
}

.picker-field-label {
  color: var(--Headings-or-Titles, #344054);
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 9px;
}

.picker-floating-label {
  position: absolute;
  top: -8px;
  left: 12px;
  background-color: #fff;
  padding: 0 4px;
color: var(--Grey, #666);
font-family: Inter;
font-size: 8px;
font-weight: 400;
line-height: 12px;
}

.picker-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  min-height: 44px;
}

.picker-trigger:focus,
.picker-trigger--open {
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05),
    0px 0px 0px 4px rgba(158, 119, 237, 0.24);
}

/* Trigger checkbox keeps its level-specific color when open */

.picker-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin-top: 0;
  transition: margin-top 0.2s ease;
}

.picker-trigger--open .picker-content {
  margin-top: 8px;
}

.picker-selected-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.picker-label {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  gap: 4px;
}

.picker-label-name {
  font-weight: 600;
}

.picker-label-description {
  font-weight: 400;
  color: #98A2B3;
}

.picker-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

/* Level-specific border colors for unselected checkboxes */
.checkbox-apprentice {
  border-color: #FF8181 !important;
  background-color: #FFEFEF !important;
}

.checkbox-professional {
  border-color: #00FF19 !important;
  background-color: #F3FFF9 !important;
}

.checkbox-coach {
  border-color: #00F0FF !important;
  background-color: #EFFEFF !important;
}

.checkbox-master {
  border-color: #00A3FF !important;
  background-color: #F2FAFF !important;
}

/* Checkmark uses semantic colors based on level */
.checkmark-apprentice path {
  stroke: #ff8181 !important;
}

.checkmark-professional path {
  stroke: #00ff19 !important;
}

.checkmark-coach path {
  stroke: #00f0ff !important;
}

.checkmark-master path {
  stroke: #00a3ff !important;
}

.picker-checkbox svg {
  display: block;
}

.picker-chevron {
  flex-shrink: 0;
  color: #667085 !important;
  font-size: 20px !important;
  transition: transform 0.2s ease;
}

.picker-chevron--rotated {
  transform: rotate(180deg);
}

.picker-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  overflow: hidden;
}

.picker-menu {
  padding: 4px 0;
}

.picker-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  outline: none;
}

.picker-menu-item:hover {
  background-color: #f9fafb;
}

.picker-menu-item:focus {
  background-color: #f9fafb;
}

/* Remove purple background from selected items - keep neutral */
.picker-menu-item--selected {
  background-color: #ffffff;
}

.picker-menu-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  justify-content: space-between;
}

.picker-menu-label {
  color: #101828;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.picker-menu-description {
  color: #98A2B3;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-transform: lowercase;
}
.picker-menu-item-content-text {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
</style>
